import React, {useEffect} from 'react';

import cs from 'classnames';
import throttle from 'lodash/throttle';
import {createPortal} from 'react-dom';
import {Link} from 'react-router';
import {Fade} from 'reactstrap';
import styled from 'styled-components';

import {useCurrentUserState} from 'app/current-user/api';
import {useUsers} from 'app/user/api';
import {UserStatusesBadges} from 'app/user/components/UserStatusesBadges';
import {getScrollableParent} from 'app/utils/browser';
import {useOutsideClick} from 'app/utils/clickoutside';
import {gt} from 'app/utils/i18n';

import {InnerFullUserInfoCard} from './InnerFullUserInfoCard';
import {InnerGuestUserInfoCard} from './InnerGuestUserInfoCard';
import {CARD_WIDTH, getPosition, H_PADDING, V_PADDING} from './positioning';

import type {BasicMaybeTaskUserSerializedType} from '../user-type';

interface Props {
  avatarCircle: JSX.Element;
  avatarRef: HTMLDivElement;
  user: BasicMaybeTaskUserSerializedType;
  show: boolean;
  toggle: () => unknown;
}

export function UserInfoCard({
  avatarCircle,
  avatarRef,
  user,
  show,
  toggle,
}: Props): JSX.Element | null {
  const currentIsGuest = useCurrentUserState().data!.is_guest_until != null;
  const cardRef = React.useRef<HTMLDivElement>(null);

  // hack to re-calculate card position when scrollable container scrolls
  const [, setUpdateCounter] = React.useState(0);
  const forceUpdate = throttle(() => setUpdateCounter((c) => c + 1), 250);

  useOutsideClick(cardRef, toggle, show);

  const {user: fullUser} = useUsers(
    {id: user.id},
    {selectFromResult: ({data}) => ({user: data?.results[0]})}
  );

  const scrollableContainer = React.useMemo(
    () => getScrollableParent(avatarRef),
    [avatarRef]
  );

  const [position, flipped, container] = getPosition(
    avatarRef,
    scrollableContainer
  );

  useEffect(() => {
    if (show && container !== scrollableContainer) {
      scrollableContainer.addEventListener('scroll', forceUpdate, {
        passive: true,
      });
      return () =>
        scrollableContainer.removeEventListener('scroll', forceUpdate);
    }
  }, [show, scrollableContainer, container]);

  const guestUntil =
    'is_guest_until' in user
      ? user.is_guest_until
      : undefined || fullUser?.is_guest_until;

  const badges = fullUser ? (
    <UserStatusesBadges
      user={fullUser}
      filter={(status) => status.showOnCard}
    />
  ) : null;

  return createPortal(
    <Fade in={show} mountOnEnter unmountOnExit className="usercard-overlay">
      <StyledContainerDiv style={position} ref={cardRef}>
        <div className={cs('main d-flex', {'flex-row-reverse': flipped})}>
          <StyledAvatarContainer setHeight={true} flipped={flipped}>
            {React.cloneElement(avatarCircle, {small: false})}
          </StyledAvatarContainer>
          <div className="flex-grow-1">
            <Link
              // @ts-expect-error it's ok to have a to={undefined}, it will render a link tag
              // without the href attribute, but the react-router TS definitions doesn't
              // know that's possible
              to={fullUser && !currentIsGuest ? fullUser.html_url : undefined}
              className="text-dark font-weight-bold"
            >
              {user.name}{' '}
              {guestUntil ? (
                <>
                  {' '}
                  <span className="badge badge-primary">{gt('guest')}</span>
                </>
              ) : null}
            </Link>
            <div className="text-secondary small">{user.email}</div>
          </div>
        </div>
        {!currentIsGuest && (fullUser || guestUntil || user.is_deleted) ? (
          <div
            className={cs('secondary d-flex', {
              'flex-row-reverse': flipped,
            })}
          >
            <StyledAvatarContainer setHeight={false} flipped={flipped} />
            <div className="flex-grow-1">
              {user.is_deleted ? (
                <span className="small text-danger">
                  {gt('No longer a member of this account.')}
                </span>
              ) : guestUntil ? (
                <InnerGuestUserInfoCard guestUntil={guestUntil} />
              ) : fullUser != null ? (
                <InnerFullUserInfoCard user={fullUser} />
              ) : null}
              {badges != null ? <div className="mt-1">{badges}</div> : null}
            </div>
          </div>
        ) : null}
      </StyledContainerDiv>
    </Fade>,
    container
  );
}

const StyledContainerDiv = styled.div`
  position: absolute;
  box-shadow: 0 0 3px 0px rgba(0, 0, 0, 0.25);
  border-radius: 4px;
  width: ${CARD_WIDTH}px;
  background: white;
  opacity: 0.97;
  overflow: hidden;
  z-index: 1000;

  .main {
    padding: ${V_PADDING}px ${H_PADDING}px;
    background-color: #f9f9f9;
  }

  .secondary {
    padding: ${V_PADDING}px ${H_PADDING}px;
  }
`;

const StyledAvatarContainer = styled.div.attrs<{
  flipped: boolean;
  setHeight: boolean;
}>(({flipped}) => ({
  className: flipped ? 'ml-3' : 'mr-3',
}))<{flipped: boolean; setHeight: boolean}>`
  width: 2.2rem;
  ${({setHeight}) => (setHeight ? 'height: 2.2rem;' : '')}
`;
